import {
  InboxSettingsServer,
  IntegrationStatus,
  InboxSettingsReadResponse,
  GmailIntegration,
  FacebookIntegration,
} from '@wix/ambassador-inbox-settings-server/http';
import { AppMarketApi } from '@wix/ambassador-app-market-api/http';

import { IntegrationEntities } from '../types';
import {
  WIX_HTML_LIVE_SITE_BASE_URL,
  INBOX_SETTINGS_URLS,
  APP_MARKET_URLS,
} from '../config';
import axios from 'axios';
import _ from 'lodash';

export interface IntegrationsServiceApi {
  getConnections(): Promise<Record<IntegrationEntities, boolean>>;
}

export class IntegrationsService implements IntegrationsServiceApi {
  private readonly inboxSettignsApi: ReturnType<typeof InboxSettingsServer>;
  private readonly appMarketApi: ReturnType<typeof AppMarketApi>;

  constructor(
    private readonly inboxBaseUrl: string,
    private readonly AppMarketBaseUrl: string,
    private readonly instance,
    private readonly msid: string,
  ) {
    this.inboxSettignsApi = InboxSettingsServer(this.inboxBaseUrl, {
      ignoredProtoHttpUrlPart: INBOX_SETTINGS_URLS.ignoredPart,
      useRedAxios: true,
    });

    this.appMarketApi = AppMarketApi(this.AppMarketBaseUrl, {
      ignoredProtoHttpUrlPart: APP_MARKET_URLS.ignoredPart,
      useRedAxios: true,
    });
  }

  private get headers() {
    return {
      authorization: this.instance,
    };
  }

  private async getEmbedData() {
    const res = await axios.get(WIX_HTML_LIVE_SITE_BASE_URL(this.msid), {
      headers: this.headers,
    });

    const data = res.data;
    return data;
  }

  public async getConnections() {
    const [inboxRes, liveSiteRes]: [
      InboxSettingsReadResponse,
      any,
    ] = await Promise.all([
      this.inboxSettignsApi
        .InboxSettingsService()(this.headers)
        .read({ fields: ['gmailIntegration,facebookIntegration'] }),
      this.getEmbedData(),
    ]);

    const {
      gmailIntegration: gmail,
      facebookIntegration: facebook,
    } = inboxRes.inboxSettings!;

    const inboxIntegrationActive = (
      integrationProp: GmailIntegration | FacebookIntegration,
    ) => integrationProp.status === IntegrationStatus.Active;

    const embedEnabled = (type: string) =>
      _.get(embedsByType, `['${type}'].enabled`, false);

    const embedsByType = _.keyBy(liveSiteRes.htmlEmbeds, 'embedType');

    return {
      [IntegrationEntities.GMAIL]: inboxIntegrationActive(gmail!),
      [IntegrationEntities.FACEBOOK_PAGE]: inboxIntegrationActive(facebook!),
      [IntegrationEntities.GOOGLE_ANALYTICS]: embedEnabled('googleAnalytics'),
      [IntegrationEntities.FACEBOOK_PIXEL]: embedEnabled('facebookPixel'),
    };
  }

  public async getFinanceIntegrationData() {
    const [{ webSolutions }, { apps: installedApps }] = await Promise.all([
      this.appMarketApi.WebSolutions()(this.headers).getWebSolutionsByTags({
        geo: 'US',
        tagsSlug: 'financial-integrations',
      }),
      this.appMarketApi
        .Apps()(this.headers)
        .getInstalledApps({ metasiteId: this.msid }),
    ]);

    return webSolutions!.map(({ baseInfo }) => ({
      slug: baseInfo!.slug!,
      description: baseInfo!.teaser!,
      name: baseInfo!.name!,
      icon: baseInfo!.icon!,
      installed: !!_.find(
        installedApps,
        (app: any) => baseInfo!.id === app.appDefinitionId,
      ),
    }));
  }
}
